/*
 * Thành phần riêng của Simba (ngoài bộ Design System chung).
 * Ảnh minh hoạ và huy hiệu — chỉ Tailwind, không CSS riêng.
 */
import { cn } from "@/lib/utils"

export const editorialVisuals = {
  journey: "/imagery/learning-journey.png",
  products: "/imagery/product-studio.png",
  concepts: "/imagery/key-concepts.png",
  community: "/imagery/community-achievements.png",
  savanna: "/imagery/savanna-friends.png",
} as const

/** Ảnh minh hoạ khổ 16:9, khai báo kích thước để trang không nhảy khi tải. */
export function EditorialVisual({
  kind,
  alt,
  priority = false,
  className,
}: {
  kind: keyof typeof editorialVisuals
  alt: string
  priority?: boolean
  className?: string
}) {
  return (
    <figure className={cn("overflow-hidden rounded-[20px] border-[1.5px] border-edge bg-paper shadow-card", className)}>
      <img
        src={editorialVisuals[kind]}
        alt={alt}
        width="1200"
        height="676"
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className="aspect-video w-full object-cover"
      />
    </figure>
  )
}

/* ---- Huy hiệu ---- */
type BadgeAnimal = "lion" | "elephant" | "zebra" | "giraffe" | "warthog" | "meerkat"

function animalForBadge(name = "", description = ""): BadgeAnimal {
  const value = `${name} ${description}`.toLocaleLowerCase("vi")
  if (/product|phiên bản|cải tiến|nâng cấp|kiến tạo|hoàn thiện/.test(value)) return "warthog"
  if (/reflection|phản tư|soi chiếu|dấu vết/.test(value)) return "elephant"
  if (/thói quen|giữ nhịp|lịch|streak/.test(value)) return "zebra"
  if (/key concept|ý tưởng|kiến thức|khái niệm/.test(value)) return "giraffe"
  if (/bạn học|đồng đội|gia đình|góp ý|tiếng nói|chủ động/.test(value)) return "meerkat"
  return "lion"
}

// Màu Savanna (token --ds-*): cam hoàng hôn, vàng thảo nguyên, kem, mực.
const FACE = "fill-[#F2C077] stroke-[#172A33] stroke-[3]"
const DETAIL = "fill-none stroke-[#172A33] stroke-[3] [stroke-linecap:round]"
const EAR = "fill-[#E5AD18] stroke-[#172A33] stroke-[3]"
const EYE = "fill-[#172A33]"

function AnimalFace({ animal }: { animal: BadgeAnimal }) {
  if (animal === "elephant")
    return (
      <>
        <path className={EAR} d="M42 71c-18-10-21 15-10 28 7 8 14 4 17-4m32-24c18-10 21 15 10 28-7 8-14 4-17-4" />
        <path className={FACE} d="M42 60c8-19 40-19 48 0 7 17-2 45-24 45S35 77 42 60Z" />
        <path className={DETAIL} d="M65 81v29c0 10 12 11 16 2" />
        <circle className={EYE} cx="55" cy="73" r="3" />
        <circle className={EYE} cx="77" cy="73" r="3" />
      </>
    )
  if (animal === "zebra")
    return (
      <>
        <path className={EAR} d="M45 57 35 39c15-4 22 7 23 18m28 0 10-18c-15-4-22 7-23 18" />
        <path className="fill-white stroke-[#172A33] stroke-[3]" d="M43 56c10-14 36-14 46 0 8 12 3 49-23 49S35 68 43 56Z" />
        <path className={DETAIL} d="m54 49 6 18m18-18-6 18M49 73l11 9m23-9-11 9m-14 17 8-13 8 13" />
        <circle className={EYE} cx="55" cy="72" r="3" />
        <circle className={EYE} cx="77" cy="72" r="3" />
      </>
    )
  if (animal === "giraffe")
    return (
      <>
        <path className={DETAIL} d="M51 53 47 37m34 16 4-16" />
        <circle className="fill-[#DB6718]" cx="46" cy="34" r="5" />
        <circle className="fill-[#DB6718]" cx="86" cy="34" r="5" />
        <path className={EAR} d="m48 58-16-7c1 14 9 18 18 16m34-9 16-7c-1 14-9 18-18 16" />
        <path className={FACE} d="M48 53c10-9 26-9 36 0l5 42c-8 15-38 15-46 0Z" />
        <circle className="fill-[#DB6718]" cx="57" cy="63" r="5" />
        <circle className="fill-[#DB6718]" cx="76" cy="81" r="5" />
        <circle className="fill-[#DB6718]" cx="57" cy="94" r="4" />
        <circle className={EYE} cx="57" cy="78" r="3" />
        <circle className={EYE} cx="77" cy="78" r="3" />
      </>
    )
  if (animal === "warthog")
    return (
      <>
        <path className={EAR} d="M47 58 31 48c-1 15 7 23 19 22m35-12 16-10c1 15-7 23-19 22" />
        <path className={FACE} d="M42 58c11-15 37-15 48 0 7 10 4 42-24 47-28-5-31-37-24-47Z" />
        <path className="fill-white stroke-[#172A33] stroke-[3]" d="M48 86c-14 0-13 17 0 15m36-15c14 0 13 17 0 15" />
        <path className={DETAIL} d="M58 91c5-4 11-4 16 0" />
        <circle className={EYE} cx="55" cy="72" r="3" />
        <circle className={EYE} cx="77" cy="72" r="3" />
      </>
    )
  if (animal === "meerkat")
    return (
      <>
        <path className={EAR} d="M47 60c-15-8-18 13-6 19m44-19c15-8 18 13 6 19" />
        <path className={FACE} d="M45 55c11-12 31-12 42 0 10 13 2 49-21 50-23-1-31-37-21-50Z" />
        <path className="fill-[#172A33]" d="M48 70c7-9 16-7 18 2 2-9 11-11 18-2-3 12-13 13-18 5-5 8-15 7-18-5Z" />
        <circle className="fill-white" cx="56" cy="70" r="3" />
        <circle className="fill-white" cx="76" cy="70" r="3" />
        <path className={DETAIL} d="M59 91c5 4 9 4 14 0" />
      </>
    )
  return (
    <>
      <path className="fill-[#DB6718] stroke-[#172A33] stroke-[3]" d="M66 35c11-9 23 2 21 12 13-2 20 12 12 22 12 8 5 24-6 25 2 13-14 21-24 13-10 9-26 2-24-11-13 0-19-16-8-24-9-10-1-25 12-23-2-11 7-21 17-14Z" />
      <path className={EAR} d="M49 61c-13-8-18 11-7 18m46-18c13-8 18 11 7 18" />
      <path className={FACE} d="M45 57c11-12 31-12 42 0 10 13 2 46-21 48-23-2-31-35-21-48Z" />
      <circle className={EYE} cx="56" cy="72" r="3" />
      <circle className={EYE} cx="76" cy="72" r="3" />
      <path className="fill-white stroke-[#172A33] stroke-[3]" d="M53 84c6-8 20-8 26 0-3 14-23 14-26 0Z" />
      <path className={DETAIL} d="m62 82 4 4 4-4m-10 12c4 3 8 3 12 0" />
    </>
  )
}

/** Huy hiệu con vật; chưa đạt thì xám mờ. */
export function BadgeMedallion({ name, description, earned = false, className }: { name?: string; description?: string; earned?: boolean; className?: string }) {
  const animal = animalForBadge(name, description)
  return (
    <svg viewBox="0 0 132 154" role="img" aria-label={`Huy hiệu ${name || ""}`} className={cn("h-auto w-24", !earned && "opacity-40 grayscale", className)}>
      <path className="fill-[#DB6718]" d="m38 112-7 36 22-10 13 16 5-39Z" />
      <path className="fill-[#A84C0F]" d="m94 112 7 36-22-10-13 16-5-39Z" />
      <path className="fill-[#E5AD18] stroke-[#172A33] stroke-[3]" d="M66 8 77 14l13-1 7 11 12 5v13l8 10-5 12 3 13-10 8-1 13-13 3-8 10-13-3-12 5-10-8-13-1-4-13-11-7 2-13-6-12 8-10V36l12-5 6-12 13 1Z" />
      <circle className="fill-[#FBEBD3] stroke-[#172A33] stroke-[3]" cx="66" cy="67" r="45" />
      <circle className="fill-white" cx="66" cy="67" r="37" />
      <g>
        <AnimalFace animal={animal} />
      </g>
      <path className="fill-none stroke-white stroke-[4] [stroke-linecap:round]" d="M36 43c8-15 20-23 35-25" />
      <circle className="fill-[#DB6718]" cx="103" cy="36" r="5" />
    </svg>
  )
}
